/**
 * 청구 일정 알림 수정 컴포넌트
 * 계약의 청구 일정 알림 정보를 수정하는 컴포넌트입니다.
 * 공문과 알림톡 종류를 설정하고 각각의 발송 여부를 관리할 수 있습니다.
 */

import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import { patchScheduleDetail_RC002 } from "src/api/billingSchedule/billingSchedule-api";
import { ContractorNoti, ContractStep, DetailTotalData, monthDays } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, BaseInput, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import BaseSection from "src/components/layout/BaseSection";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useModal } from "src/recoil/modalState/hook";
import { BULLET } from "src/utils";

const NoEditStateNoS2: Array<string> = [ContractStep.APPLY_CANCEL, ContractStep.TERMINATE_RECEIVED, ContractStep.TERMINATE_COMPLETE];
const NoEditStateS2: Array<string> = NoEditStateNoS2.concat([
  ContractStep.CONTRACT_ACCEPT,
  ContractStep.USE_APPROVAL,
  ContractStep.USE_PROGRESS,
  ContractStep.USE_COMPLETE,
]);
// 공간임대(FULL_COURT)는 해지접수/해지완료 상태에서도 편집 가능 (신청취소만 비활성)
const NoEditStateFullCourt: Array<string> = [ContractStep.APPLY_CANCEL];

/**
 * 청구 일정 알림 수정 컴포넌트의 props 인터페이스
 */
type Props = {
  detailData: DetailTotalData; // 상세 데이터
  setData: Function; // 데이터 업데이트 함수
};

interface ScheduleRequest {
  isUseTaxInvoice: boolean;
  isAutoTaxInvoice: boolean;
  taxInvoiceIssueMonth: string;
  taxInvoiceIssueDay: number;
  billContactList: Array<ContractorNoti>;
}

const TAX_INVOICE_MONTH_OPTIONS = [
  { label: "전월 기준", value: "PREV_MONTH" },
  { label: "당월 기준", value: "CURR_MONTH" },
  { label: "익월 기준", value: "NEXT_MONTH" },
];

const ContractTaxInvoiceSection = ({ detailData, setData }: Props) => {
  const { contract, scheduleList, billContactList, provider } = detailData;

  const disabledState = useMemo(
    () => [String(detailData?.contract?.contractStep), String(detailData?.product?.productType)],
    [detailData?.contract?.contractStep, detailData?.product?.productType],
  );

  // 편집 버튼 비활성화 여부 (공간임대는 해지접수/해지완료에도 편집 가능)
  const isEditButtonDisabled = useMemo(() => {
    const [step, productType] = disabledState;
    if (productType === "TIME_COURT") {
      return !NoEditStateS2.includes(step);
    }
    const noEditList = productType === "FULL_COURT" ? NoEditStateFullCourt : NoEditStateNoS2;
    return noEditList.includes(step);
  }, [disabledState]);

  const [editable, setEditable] = useState(false);

  const { setBaseModal } = useModal();

  const { control, setValue, watch, handleSubmit } = useForm<ScheduleRequest>({
    defaultValues: {
      isUseTaxInvoice: false,
      isAutoTaxInvoice: false,
      taxInvoiceIssueMonth: "",
      taxInvoiceIssueDay: 0,
      billContactList: [],
    },
  });

  const { executeAsync: editSchedule } = useApiOperation(patchScheduleDetail_RC002);

  const { isAuthority } = usePartnerAuthority();

  // 세금계산서 발행 비활성화 조건
  const taxInvoiceDisabled = useMemo(() => {
    const step = contract?.contractStep;
    // 공간임대(FULL_COURT)는 이용완료/해지완료에도 세금계산서 설정 수정 가능
    if (detailData?.product?.productType === "FULL_COURT" && (step === "USE_COMPLETE" || step === "TERMINATE_COMPLETE")) {
      return false;
    }
    return step === "APPLY_CANCEL" || step === "TERMINATE_COMPLETE" || step === "APPLY_CONFIRM" || step === "USE_COMPLETE";
  }, [contract?.contractStep, detailData?.product?.productType]);

  const initTaxInvoiceData = () => {
    const rentalSchedule = scheduleList?.find((sd: BillingDetail) => sd.supplyType === "RENTAL");
    const data = {
      isUseTaxInvoice: rentalSchedule?.isUseTaxInvoice,
      isAutoTaxInvoice: rentalSchedule?.isAutoTaxInvoice,
      taxInvoiceIssueMonth: rentalSchedule?.taxInvoiceIssueMonth,
      taxInvoiceIssueDay: rentalSchedule?.taxInvoiceIssueDay,
      billContactList: billContactList?.filter((item) => item.receiveNotiType === "TAX_INVOICE"),
    };

    setValue("isUseTaxInvoice", data.isUseTaxInvoice || false);
    setValue("isAutoTaxInvoice", data.isAutoTaxInvoice || false);
    setValue("taxInvoiceIssueMonth", data.taxInvoiceIssueMonth || "");
    setValue("taxInvoiceIssueDay", data.taxInvoiceIssueDay || 0);
    setValue("billContactList", data.billContactList || ([] as Array<ContractorNoti>));
  };

  useEffect(() => {
    initTaxInvoiceData();
  }, [scheduleList]);

  // 이메일 추가
  const handleAddEmail = () => {
    const currentList = watch("billContactList");
    const activeEmails = currentList.filter((item) => item.cmdType !== "D");

    if (activeEmails.length >= 3) {
      return;
    }

    setValue("billContactList", [
      ...currentList,
      {
        email: "",
        receiveNotiType: "TAX_INVOICE",
        cmdType: "C",
        isDeleted: false,
      },
    ]);
  };

  // 이메일 삭제 (cmdType: "D"로 변경)
  const handleRemoveEmail = (index: number) => {
    const currentList = watch("billContactList");

    const updatedList = currentList.map((item, idx) => {
      if (idx === index) {
        return { ...item, cmdType: "D" };
      }
      return item;
    });

    setValue("billContactList", updatedList);
  };

  const onConfirmTaxInvoiceSubmit = handleSubmit(async () => {
    setBaseModal({
      isOpen: true,
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
      title: "세금계산서 발행 설정",
      children: "저장하시겠습니까?",
      onClick: () => {
        onSaveBillingsData();
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  });

  // 저장
  const onSaveBillingsData = handleSubmit(async (formData) => {
    // NotificationRow를 원본 ScheduleNoti[] 형태로 변환
    const billContactList = formData.billContactList.map((item) => {
      return {
        ...item,
        cmdType: item.cmdType ? item.cmdType : item.billContactId ? "U" : "C",
      };
    });
    const scheduleId = detailData?.scheduleList?.find((schedule: BillingDetail) => schedule.supplyType === "RENTAL")?.scheduleId;
    try {
      await editSchedule({
        contractId: Number(detailData?.contract?.contractId),
        billContactList: billContactList,
        scheduleList: [
          {
            scheduleId: scheduleId,
            isUseTaxInvoice: formData.isUseTaxInvoice,
            isAutoTaxInvoice: formData.isAutoTaxInvoice,
            taxInvoiceIssueMonth: formData.taxInvoiceIssueMonth,
            taxInvoiceIssueDay: formData.taxInvoiceIssueDay,
          },
        ],
      });

      setEditable(false);
      setData();
    } catch (error) {
      console.error("저장 실패:", error);
    }
  });

  return (
    <article className={`contents-container__wrap-article full-width `}>
      {isAuthority("w") && (
        <div className="contents-container__edit-area">
          {editable ? (
            <div className="flex-center">
              <BaseButton
                type="button"
                className="size-small color-white mr10"
                title={"취소"}
                onClick={() => {
                  setEditable(false);
                  // 폼 리셋
                  initTaxInvoiceData();
                }}
              />
              <BaseButton type="button" className="size-small" title={"저장"} onClick={onConfirmTaxInvoiceSubmit} />
            </div>
          ) : (
            <button className="base-edit-btn" onClick={() => setEditable(true)} disabled={isEditButtonDisabled} />
          )}
        </div>
      )}
      <div className="mb20">
        <BaseSectionTitle title={"세금계산서 설정"} className="pb10" />

        <p className="font12 text-primary3">{BULLET} 면세인 경우 계산서가 발행됩니다.</p>
      </div>

      <div>
        <BaseSection
          title={"발행 여부"}
          children={
            <div className="contents-container__grid-contents">
              {editable && (
                <div className="flex-row flex-center-start">
                  <BaseRadio
                    id={"isUseTaxInvoice_true"}
                    disabled={taxInvoiceDisabled}
                    name={"isUseTaxInvoice"}
                    label="사용"
                    value="true"
                    className="mr16"
                    checked={watch("isUseTaxInvoice") === true}
                    onChange={() => {
                      if (provider?.provider?.isVerifiedTaxInvoice === false) {
                        setBaseModal({
                          isOpen: true,
                          title: `세금계산서 발행 설정에 실패했습니다.`,
                          children: "해당 프로바이더의 세금계산서 발행 설정이 비활성화되어 있습니다. 활성화하려면 관리자에게 문의하세요. ",
                          btnRightTitle: "확인",
                        });
                        return;
                      }
                      setValue("isUseTaxInvoice", true);
                    }}
                  />
                  <BaseRadio
                    disabled={taxInvoiceDisabled}
                    id={"isUseTaxInvoice_false"}
                    name={"isUseTaxInvoice"}
                    label="사용 안함"
                    value="false"
                    checked={watch("isUseTaxInvoice") === false}
                    onChange={() => {
                      setValue("isUseTaxInvoice", false);
                      setValue("isAutoTaxInvoice", false);
                    }}
                  />
                </div>
              )}
              {!editable && (
                <div className="">
                  <span>{watch("isUseTaxInvoice") ? "사용" : "사용 안함"}</span>
                </div>
              )}
            </div>
          }
        />
        <div
          className="mb20 pa20"
          style={{
            background: "#F8F8FA",
            borderRadius: "10px",
            overflowY: "auto",
          }}
        >
          <BaseSection
            title={"자동 발행"}
            children={
              <div className="contents-container__grid-contents">
                {editable && (
                  <div className="flex-row flex-center-start">
                    <BaseRadio
                      id={"isAutoTaxInvoice_true"}
                      disabled={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                      name={"isAutoTaxInvoice"}
                      label="사용"
                      value="true"
                      className="mr16"
                      checked={watch("isAutoTaxInvoice") === true}
                      onChange={() => {
                        setValue("isAutoTaxInvoice", true);
                      }}
                    />
                    <BaseRadio
                      disabled={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                      id={"isAutoTaxInvoice_false"}
                      name={"isAutoTaxInvoice"}
                      label="사용 안함"
                      value="false"
                      checked={watch("isAutoTaxInvoice") === false}
                      onChange={() => {
                        setValue("isAutoTaxInvoice", false);
                      }}
                    />
                  </div>
                )}
                {!editable && (
                  <div className="">
                    <span>{watch("isAutoTaxInvoice") ? "사용" : "사용 안함"}</span>
                  </div>
                )}
              </div>
            }
          />
          <BaseSection
            titleChildren={
              <div className="flex-center font14 font-weight-semibold text-primary3">
                <span>발행일</span>
                <BaseTooltip
                  contents={""}
                  tooltip={"세금계산서 발행일 변경 시, 10일 이내인 경우 다음달부터 변경일로 적용됩니다."}
                  type={"normal"}
                  className={"tooltip__trigger-icon"}
                />
              </div>
            }
            children={
              <div className="contents-container__grid-contents">
                {editable && (
                  <div className="flex-row flex-center-start">
                    <div className="mr10 minmax140">
                      <BaseSelect
                        isDisabled={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                        name={"taxInvoiceIssueMonth"}
                        placeholder={""}
                        value={watch("taxInvoiceIssueMonth")}
                        stateOptions={TAX_INVOICE_MONTH_OPTIONS}
                        setStateValue={(value: string) => {
                          setValue("taxInvoiceIssueMonth", value);
                        }}
                        menuPosition="fixed"
                      />
                    </div>
                    <div className="minmax100">
                      <BaseSelect
                        isDisabled={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                        name={"taxInvoiceIssueDay"}
                        placeholder={""}
                        value={watch("taxInvoiceIssueDay")}
                        stateOptions={monthDays}
                        setStateValue={(value: number) => {
                          setValue("taxInvoiceIssueDay", value);
                        }}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                )}
                {!editable && (
                  <div className="">
                    <span>
                      {TAX_INVOICE_MONTH_OPTIONS.find((item) => item.value === watch("taxInvoiceIssueMonth"))?.label} / {watch("taxInvoiceIssueDay")}
                      일
                    </span>
                  </div>
                )}
              </div>
            }
          />
          <BaseSection
            title={editable ? `이메일 (${watch("billContactList")?.filter((item) => item.cmdType !== "D").length}/3)` : `이메일`}
            required={editable}
            children={
              <div className="minmax300">
                {watch("billContactList")
                  ?.map((item: ContractorNoti, idx: number) => ({ ...item, originalIndex: idx }))
                  ?.filter((item: ContractorNoti) => item.cmdType !== "D")
                  ?.map((item: ContractorNoti, _, array) => (
                    <div key={item.billContactId || item.originalIndex}>
                      {editable && (
                        <div className="flex-center mb10">
                          <Controller
                            name={`billContactList.${item.originalIndex}.email`}
                            control={control}
                            rules={{
                              validate: (value) => {
                                const currentItem = watch(`billContactList.${item.originalIndex}`);
                                // cmdType이 "D"면 validation 건너뛰기
                                if (currentItem?.cmdType === "D") return true;

                                if (!value) return "이메일을 입력해주세요";
                                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                  return "올바른 이메일 형식이 아닙니다";
                                }
                                return true;
                              },
                            }}
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                              <>
                                <BaseInput
                                  className="minmax240"
                                  onChange={onChange}
                                  value={value}
                                  name={name}
                                  placeholder="이메일을 입력하세요"
                                  readonly={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                                  errorText={error?.message}
                                />
                                <div className="flex-center ml10">
                                  {(() => {
                                    const visibleEmails =
                                      watch("billContactList")
                                        ?.map((item: ContractorNoti, idx: number) => ({ ...item, originalIndex: idx }))
                                        ?.filter((item: ContractorNoti) => item.cmdType !== "D") || [];

                                    const isFirstItem = visibleEmails.findIndex((v) => v.originalIndex === item.originalIndex) === 0;
                                    const canDelete = visibleEmails.length > 1;

                                    return isFirstItem ? (
                                      <>
                                        <button
                                          className={`plus-btn mr10 absolute ${error ? "mb20" : ""}`}
                                          disabled={taxInvoiceDisabled || !watch("isUseTaxInvoice")}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleAddEmail();
                                          }}
                                        />

                                        <button
                                          className={`minus-btn  ${error ? "mb20" : ""}`}
                                          disabled={taxInvoiceDisabled || !watch("isUseTaxInvoice") || !canDelete}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            if (!canDelete) return;
                                            handleRemoveEmail(item.originalIndex);
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <button
                                        className={`minus-btn absolute ${error ? "mb20" : ""}`}
                                        disabled={taxInvoiceDisabled || !watch("isUseTaxInvoice") || !canDelete}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleRemoveEmail(item.originalIndex);
                                        }}
                                      />
                                    );
                                  })()}
                                </div>
                              </>
                            )}
                          />
                        </div>
                      )}
                      {!editable && (
                        <div className="my10">
                          <span>{item.email}</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            }
          />
        </div>
      </div>
    </article>
  );
};
export default ContractTaxInvoiceSection;
